import type { AdminCategory, AdminProduct, InventoryEntry, InventoryRow } from '@/types/admin';
import { slugify } from '@/data/adminSeed';
import { ServiceError } from '../../contracts';
import type { AdminCategoryService, AdminInventoryService, AdminProductService } from '../../adminContracts';
import { ctx, delay, matches, nowIso, paginate, rememberKey, saveAdmin, seenKey, stockStatusOf, uid, type TenantData } from './context';

const invalid = (m: string) => new ServiceError(m, 'INVALID');
const notFound = (w: string) => new ServiceError(`${w} not found`, 'NOT_FOUND');

const sumVariants = (p: Pick<AdminProduct, 'variants'>) => p.variants.reduce((s, v) => s + v.stock, 0);

function withCounts(d: TenantData): AdminCategory[] {
  return d.categories.map((c) => ({ ...c, productCount: d.products.filter((p) => p.categoryId === c.id && p.status !== 'ARCHIVED').length }));
}

function validateProduct(d: TenantData, input: Parameters<AdminProductService['create']>[0], selfId?: string) {
  if (!input.name.trim()) throw invalid('Product name is required');
  if (!(input.price > 0)) throw invalid('Price must be greater than zero');
  if (input.compareAtPrice != null && input.compareAtPrice <= input.price) throw invalid('Compare-at price must be higher than the selling price');
  if (input.costPrice != null && input.costPrice < 0) throw invalid('Cost cannot be negative');
  if (!d.categories.some((c) => c.id === input.categoryId)) throw invalid('Choose a valid category');
  if (!input.sku.trim()) throw invalid('SKU is required');
  if (d.products.some((p) => p.id !== selfId && p.sku.toLowerCase() === input.sku.trim().toLowerCase())) throw new ServiceError('Another product already uses this SKU', 'CONFLICT');
  const slug = input.slug || slugify(input.name);
  if (d.products.some((p) => p.id !== selfId && p.slug === slug)) throw new ServiceError('Another product already uses this URL slug', 'CONFLICT');
  input.variants.forEach((v) => { if (!v.sku.trim() || v.price <= 0 || v.stock < 0) throw invalid('Every variant needs a SKU, a price above zero and non-negative stock'); });
  return slug;
}

function ledger(d: TenantData, p: AdminProduct, delta: number, before: number, reason: InventoryEntry['reason'], note: string | undefined, actorName: string): InventoryEntry {
  const e: InventoryEntry = {
    id: uid('inv'), tenantId: d.tenantId, productId: p.id, productName: p.name, sku: p.sku, delta, before, after: before + delta, reason, note, actorName, createdAt: nowIso(),
  };
  d.inventory.push(e);
  return e;
}

export const adminProducts: AdminProductService = {
  async list(q) {
    const { data: d } = ctx('catalog.read', 'products');
    let items = d.products.filter((p) =>
      matches(q.q, p.name, p.sku, p.brand, ...p.tags)
      && (!q.categoryId || p.categoryId === q.categoryId)
      && (!q.status || p.status === q.status)
      && (!q.stock || q.stock === 'all' || (q.stock === 'in' ? p.stock > p.lowStockThreshold : q.stock === 'low' ? p.stock > 0 && p.stock <= p.lowStockThreshold : p.stock <= 0)));
    switch (q.sort) {
      case 'name': items.sort((a, b) => a.name.localeCompare(b.name)); break;
      case 'price-asc': items.sort((a, b) => a.price - b.price); break;
      case 'price-desc': items.sort((a, b) => b.price - a.price); break;
      case 'stock': items.sort((a, b) => a.stock - b.stock); break;
      default: items.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    }
    items = items.slice();
    return delay(paginate(items, q.page, q.pageSize ?? 10));
  },
  async get(id) {
    const { data: d } = ctx('catalog.read', 'products');
    const p = d.products.find((x) => x.id === id);
    if (!p) throw notFound('Product'); // another tenant's id lands here too: indistinguishable from missing
    return delay(p);
  },
  async create(input) {
    const { actor, data: d } = ctx('catalog.write', 'products');
    const slug = validateProduct(d, input);
    const now = nowIso();
    const stock = input.variants.length ? sumVariants(input) : input.stock;
    const p: AdminProduct = {
      ...input, slug, stock, id: uid('prod'), tenantId: d.tenantId, specs: [], rating: 0, reviewCount: 0, createdAt: now, updatedAt: now,
    };
    d.products.unshift(p);
    if (stock > 0) ledger(d, p, stock, 0, 'INITIAL', 'Opening stock', actor.user.name);
    saveAdmin();
    return delay(p, 600);
  },
  async update(id, input) {
    const { actor, data: d } = ctx('catalog.write', 'products');
    const i = d.products.findIndex((x) => x.id === id);
    if (i < 0) throw notFound('Product');
    const prev = d.products[i];
    const slug = validateProduct(d, input, id);
    const stock = input.variants.length ? sumVariants(input) : input.stock;
    const next: AdminProduct = { ...prev, ...input, slug, stock, updatedAt: nowIso() };
    // Stock edits through the form are still ledgered, never silent.
    if (stock !== prev.stock) ledger(d, next, stock - prev.stock, prev.stock, 'CORRECTION', 'Edited in product form', actor.user.name);
    d.products[i] = next;
    saveAdmin();
    return delay(next, 600);
  },
  async remove(id) {
    const { data: d } = ctx('catalog.write', 'products');
    const p = d.products.find((x) => x.id === id);
    if (!p) throw notFound('Product');
    if (d.orders.some((o) => o.items.some((it) => it.productId === id))) {
      throw new ServiceError('This product has orders and cannot be deleted. Archive it instead.', 'CONFLICT');
    }
    d.products = d.products.filter((x) => x.id !== id);
    saveAdmin();
    return delay(undefined, 400);
  },
  async bulk(ids, action) {
    const { data: d } = ctx('catalog.write', 'products');
    let affected = 0;
    const blocked: string[] = [];
    for (const id of ids) {
      const p = d.products.find((x) => x.id === id);
      if (!p) continue;
      if (action === 'DELETE') {
        if (d.orders.some((o) => o.items.some((it) => it.productId === id))) { blocked.push(p.name); continue; }
        d.products = d.products.filter((x) => x.id !== id);
      } else {
        p.status = action === 'ACTIVATE' ? 'ACTIVE' : action === 'DRAFT' ? 'DRAFT' : 'ARCHIVED';
        p.updatedAt = nowIso();
      }
      affected++;
    }
    saveAdmin();
    if (blocked.length && !affected) throw new ServiceError(`Cannot delete products that have orders: ${blocked.slice(0, 2).join(', ')}${blocked.length > 2 ? '…' : ''}. Archive them instead.`, 'CONFLICT');
    return delay({ affected }, 500);
  },
};

export const adminCategories: AdminCategoryService = {
  async list(q) {
    const { data: d } = ctx('catalog.read', 'categories');
    const list = withCounts(d).filter((c) => matches(q?.q, c.name, c.slug) && (!q?.status || q.status === 'all' || c.status === q.status));
    return delay(list.sort((a, b) => a.sortOrder - b.sortOrder));
  },
  async create(input) {
    const { data: d } = ctx('catalog.write', 'categories');
    const slug = input.slug || slugify(input.name);
    if (!input.name.trim()) throw invalid('Category name is required');
    if (d.categories.some((c) => c.slug === slug)) throw new ServiceError('Another category already uses this slug', 'CONFLICT');
    if (input.parentId && d.categories.find((c) => c.id === input.parentId)?.parentId) throw invalid('Only one level of sub-categories is supported');
    const c: AdminCategory = { ...input, slug, id: uid('cat'), tenantId: d.tenantId, sortOrder: d.categories.length, productCount: 0 };
    d.categories.push(c);
    saveAdmin();
    return delay(c, 500);
  },
  async update(id, input) {
    const { data: d } = ctx('catalog.write', 'categories');
    const i = d.categories.findIndex((c) => c.id === id);
    if (i < 0) throw notFound('Category');
    const slug = input.slug || slugify(input.name);
    if (d.categories.some((c) => c.id !== id && c.slug === slug)) throw new ServiceError('Another category already uses this slug', 'CONFLICT');
    if (input.parentId === id) throw invalid('A category cannot be its own parent');
    if (input.parentId && d.categories.find((c) => c.id === input.parentId)?.parentId) throw invalid('Only one level of sub-categories is supported');
    if (input.parentId && d.categories.some((c) => c.parentId === id)) throw invalid('This category has sub-categories, so it cannot become one itself');
    d.categories[i] = { ...d.categories[i], ...input, slug };
    saveAdmin();
    return delay(withCounts(d)[i], 500);
  },
  async remove(id) {
    const { data: d } = ctx('catalog.write', 'categories');
    if (!d.categories.some((c) => c.id === id)) throw notFound('Category');
    if (d.products.some((p) => p.categoryId === id)) throw new ServiceError('This category still contains products. Move or delete them first.', 'CONFLICT');
    if (d.categories.some((c) => c.parentId === id)) throw new ServiceError('This category has sub-categories. Remove them first.', 'CONFLICT');
    d.categories = d.categories.filter((c) => c.id !== id);
    saveAdmin();
    return delay(undefined, 400);
  },
};

export const adminInventory: AdminInventoryService = {
  async list(q) {
    const { data: d } = ctx('catalog.read', 'products');
    const catName = (id: string) => d.categories.find((c) => c.id === id)?.name ?? '—';
    const rows: InventoryRow[] = d.products
      .filter((p) => p.status !== 'ARCHIVED' && matches(q.q, p.name, p.sku) && (!q.categoryId || p.categoryId === q.categoryId))
      .map((p) => ({
        productId: p.id, name: p.name, sku: p.sku, categoryName: catName(p.categoryId), stock: p.stock, lowStockThreshold: p.lowStockThreshold,
        status: stockStatusOf(p.stock, p.lowStockThreshold), icon: p.icon, hue: p.hue, image: p.images[0],
      }))
      .filter((r) => !q.status || q.status === 'all' || r.status === q.status)
      .sort((a, b) => a.stock - b.stock);
    return delay(paginate(rows, q.page, q.pageSize ?? 10));
  },
  async adjust(input) {
    const { actor, data: d } = ctx('inventory.adjust', 'products');
    const prior = seenKey(d, input.idempotencyKey);
    if (prior) return delay(d.inventory.find((e) => e.id === prior)!, 300); // replay: same result, no double-apply
    if (!Number.isInteger(input.delta) || input.delta === 0) throw invalid('Quantity must be a whole number other than zero');
    if (!input.reason) throw invalid('Choose a reason for this adjustment');
    const p = d.products.find((x) => x.id === input.productId);
    if (!p) throw notFound('Product');
    if (p.stock + input.delta < 0) throw new ServiceError(`Cannot remove ${Math.abs(input.delta)} — only ${p.stock} in stock.`, 'CONFLICT');
    const before = p.stock;
    if (p.variants.length) {
      let remaining = input.delta;
      if (remaining > 0) p.variants[0].stock += remaining;
      else for (const v of p.variants) { const take = Math.min(v.stock, -remaining); v.stock -= take; remaining += take; if (!remaining) break; }
    }
    p.stock = before + input.delta;
    p.updatedAt = nowIso();
    const e = ledger(d, p, input.delta, before, input.reason, input.note?.trim() || undefined, actor.user.name);
    rememberKey(d, input.idempotencyKey, e.id);
    saveAdmin();
    return delay(e, 500);
  },
  async history(q) {
    const { data: d } = ctx('catalog.read', 'products');
    const rows = d.inventory.filter((e) => (!q.productId || e.productId === q.productId) && matches(q.q, e.productName, e.sku, e.note, e.actorName)).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return delay(paginate(rows, q.page, q.pageSize ?? 10));
  },
};
